import {DepositoryType} from '@transactions/graphql';
import type {
  AccountBalancesQuery,
  AccountListItemFragment,
} from '@transactions/graphql';

/**
 * Balance data for one account, as delivered by the AccountBalances query.
 * homeBalance/balanceStaleSince exist only on depository accounts;
 * balanceAsOf (the date of the observation backing the balance) only on
 * investment accounts.
 */
export type AccountBalanceInfo = {
  balance: number;
  homeBalance?: number | null;
  balanceStaleSince?: string | null;
  balanceAsOf?: string | null;
  /** Any raw activity in the last year (drives the derived inactive state). */
  hasRecentActivity?: boolean;
};

export type BalancesById = ReadonlyMap<string, AccountBalanceInfo>;

/**
 * Index an AccountBalances result by account id. Account metadata renders
 * before this query returns, so consumers must treat a missing entry as
 * "not loaded yet", never as zero.
 */
export const indexBalances = (
  data: AccountBalancesQuery | undefined,
): BalancesById =>
  new Map<string, AccountBalanceInfo>(
    (data?.allAccounts ?? []).map((account) => [account._id, account]),
  );

/** One row's contribution to a section's count and subtotal. */
export type SectionAccount = {
  /**
   * Native stored balance, or null while balances are still loading — they
   * stream in after the account list renders.
   */
  balance: number | null;
  currency: string;
  /**
   * Balance in USD cents (equal to balance for USD accounts, converted for
   * foreign wallets, null while a wallet has no conversion). Absent on
   * account types that are always USD (investment accounts).
   */
  homeBalance?: number | null;
  /** Rows render card debt as positive "owed"; see toDisplayBalance. */
  creditCard: boolean;
};

/**
 * Merge metadata rows with their balances for AccountSection, which needs
 * balance/currency/homeBalance per row to compute the section subtotal.
 * Accounts whose balance has not arrived get null, which suppresses the
 * subtotal rather than understating it.
 */
export const toSectionAccounts = (
  accounts: readonly AccountListItemFragment[],
  balances: BalancesById,
): SectionAccount[] =>
  accounts.map((account) => {
    const info = balances.get(account._id);
    return {
      currency: account.currency,
      balance: info?.balance ?? null,
      homeBalance: info?.homeBalance,
      creditCard:
        account.__typename === 'DepositoryAccount' &&
        account.depositoryType === DepositoryType.CreditCard,
    };
  });
